import { BarChart3, Minus, Plus, Shuffle } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const ReelEditor = ({
  reelSet,
  onUpdate,
  symbols = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'SE1', 'SE2', 'SC1', 'SC2'],
}) => {
  const [localReelSet, setLocalReelSet] = useState(reelSet);
  const [showStats, setShowStats] = useState(false);

  // Debounced update до батьківського компонента
  useEffect(() => {
    const timer = setTimeout(() => {
      onUpdate(localReelSet);
    }, 300);
    return () => clearTimeout(timer);
  }, [localReelSet, onUpdate]);

  // Оновлення символу в конкретній позиції
  const updateSymbol = (reelIndex, symbolIndex, newSymbol) => {
    const newLayout = [...localReelSet.layout];
    newLayout[reelIndex] = [...newLayout[reelIndex]];
    newLayout[reelIndex][symbolIndex] = newSymbol;
    setLocalReelSet((prev) => ({ ...prev, layout: newLayout }));
  };

  // Зміна довжини reel
  const resizeReel = (reelIndex, newLength) => {
    if (newLength < 1) return;
    const newLayout = [...localReelSet.layout];
    const currentReel = newLayout[reelIndex];

    if (newLength > currentReel.length) {
      // Додаємо символи
      const toAdd = newLength - currentReel.length;
      newLayout[reelIndex] = [...currentReel, ...Array(toAdd).fill(symbols[0])];
    } else {
      // Обрізаємо
      newLayout[reelIndex] = currentReel.slice(0, newLength);
    }
    setLocalReelSet((prev) => ({ ...prev, layout: newLayout }));
  };

  // Копіювання reel
  const copyReel = (fromIndex, toIndex) => {
    const newLayout = [...localReelSet.layout];
    newLayout[toIndex] = [...newLayout[fromIndex]];
    setLocalReelSet((prev) => ({ ...prev, layout: newLayout }));
  };

  // Випадкове заповнення reel
  const randomizeReel = (reelIndex) => {
    const newLayout = [...localReelSet.layout];
    const length = newLayout[reelIndex].length;
    newLayout[reelIndex] = Array(length)
      .fill(0)
      .map(() => symbols[Math.floor(Math.random() * symbols.length)]);
    setLocalReelSet((prev) => ({ ...prev, layout: newLayout }));
  };

  // Статистика символів
  const getSymbolStats = (reelIndex) => {
    const reel = localReelSet.layout[reelIndex];
    const stats = {};
    reel.forEach((symbol) => {
      stats[symbol] = (stats[symbol] || 0) + 1;
    });
    return stats;
  };

  return (
    <div className="p-4 bg-white border border-gray-300 rounded-lg">
      {/* Заголовок */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Reel id: {localReelSet.id}
            <span className="px-2 py-1 ml-2 text-xs text-blue-800 bg-blue-100 rounded">{localReelSet.type}</span>
          </h3>
          <p className="text-sm text-gray-600">
            {localReelSet.layout.length} reels, довжина: {localReelSet.layout.map((r) => r.length).join(', ')}
          </p>
        </div>
        <button
          onClick={() => setShowStats(!showStats)}
          className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
        >
          <BarChart3 className="w-4 h-4" />
          Статистика
        </button>
      </div>

      {/* Reels Grid */}
      <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-5">
        {localReelSet.layout.map((reel, reelIndex) => (
          <div key={reelIndex} className="p-3 rounded bg-gray-50">
            {/* Заголовок reel */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Reel {reelIndex + 1}</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={reel.length}
                  onChange={(e) => resizeReel(reelIndex, parseInt(e.target.value) || 1)}
                  className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded"
                  min="1"
                />
              </div>
            </div>

            {/* Утиліти reel */}
            <div className="flex gap-1 mb-2">
              <button
                onClick={() => randomizeReel(reelIndex)}
                className="p-1 text-purple-600 bg-purple-100 rounded hover:bg-purple-200"
                title="Випадково заповнити"
              >
                <Shuffle className="w-3 h-3" />
              </button>
              <select
                onChange={(e) => e.target.value && copyReel(parseInt(e.target.value), reelIndex)}
                value=""
                className="text-xs border border-gray-300 rounded"
                title="Копіювати з іншого reel"
              >
                <option value="">Копія</option>
                {localReelSet.layout.map(
                  (_, idx) =>
                    idx !== reelIndex && (
                      <option key={idx} value={idx}>
                        Reel {idx + 1}
                      </option>
                    ),
                )}
              </select>
            </div>

            {/* Символи */}
            <div className="space-y-1 overflow-y-auto max-h-64">
              {reel.map((symbol, symbolIndex) => (
                <div key={symbolIndex} className="flex items-center gap-1">
                  <span className="w-6 text-xs text-gray-500">{symbolIndex}</span>
                  <select
                    value={symbol}
                    onChange={(e) => updateSymbol(reelIndex, symbolIndex, e.target.value)}
                    className="flex-1 text-xs border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {symbols.map((sym) => (
                      <option key={sym} value={sym}>
                        {sym}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-0.5">
                    <button
                      onClick={() => {
                        const newLayout = [...localReelSet.layout];
                        newLayout[reelIndex] = [
                          ...newLayout[reelIndex].slice(0, symbolIndex + 1),
                          symbols[0],
                          ...newLayout[reelIndex].slice(symbolIndex + 1),
                        ];
                        setLocalReelSet((prev) => ({
                          ...prev,
                          layout: newLayout,
                        }));
                      }}
                      className="p-0.5 bg-green-100 hover:bg-green-200 text-green-600 rounded text-xs"
                      title="Додати символ після"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => {
                        if (reel.length > 1) {
                          const newLayout = [...localReelSet.layout];
                          newLayout[reelIndex] = newLayout[reelIndex].filter((_, idx) => idx !== symbolIndex);
                          setLocalReelSet((prev) => ({
                            ...prev,
                            layout: newLayout,
                          }));
                        }
                      }}
                      className="p-0.5 bg-red-100 hover:bg-red-200 text-red-600 rounded text-xs"
                      disabled={reel.length <= 1}
                      title="Видалити символ"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Статистика reel */}
            {showStats && (
              <div className="mt-2 text-xs">
                <div className="mb-1 font-medium text-gray-600">Частота:</div>
                {Object.entries(getSymbolStats(reelIndex))
                  .sort(([, a], [, b]) => b - a)
                  .map(([symbol, count]) => (
                    <div key={symbol} className="flex justify-between">
                      <span>{symbol}:</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Глобальні утиліти */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
        <button
          onClick={() => {
            const newLayout = localReelSet.layout.map((reel) =>
              reel.map(() => symbols[Math.floor(Math.random() * symbols.length)]),
            );
            setLocalReelSet((prev) => ({ ...prev, layout: newLayout }));
          }}
          className="flex items-center gap-1 px-3 py-1 text-sm text-purple-700 bg-purple-100 rounded hover:bg-purple-200"
        >
          <Shuffle className="w-4 h-4" />
          Випадково всі
        </button>

        <select
          onChange={(e) => {
            if (e.target.value) {
              const length = parseInt(e.target.value);
              const newLayout = [...localReelSet.layout];
              for (let index = 0; index < newLayout.length; index++) {
                const currentReel = newLayout[index];
                if (length > currentReel.length) {
                  const toAdd = length - currentReel.length;
                  newLayout[index] = [...currentReel, ...Array(toAdd).fill(symbols[0])];
                } else {
                  newLayout[index] = currentReel.slice(0, length);
                }
              }
              setLocalReelSet((prev) => ({ ...prev, layout: newLayout }));
            }
          }}
          value=""
          className="px-2 py-1 text-sm border border-gray-300 rounded"
        >
          <option value="">Встановити довжину всім</option>
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="150">150</option>
          <option value="200">200</option>
        </select>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Загально символів: {localReelSet.layout.reduce((acc, reel) => acc + reel.length, 0)}</span>
        </div>
      </div>
    </div>
  );
};

export default ReelEditor;
